
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTags } from "@/hooks/useTags";
import { useHiddenTags } from "@/hooks/useHiddenTags";
import AddTagButton from "@/components/common/AddTagButton";
import { SidebarMenu } from "@/components/ui/sidebar/components/menu";
import { SidebarGroupContent } from "@/components/ui/sidebar/components/SidebarGroup";
import SidebarSection from "@/components/ui/sidebar/components/SidebarSection";
import TagList from "@/components/tags/TagList";
import TagFilterControls from "@/components/tags/TagFilterControls";
import EditTagDialog from "@/components/tags/EditTagDialog";
import DeleteTagDialog from "@/components/tags/DeleteTagDialog";
import { useDragAndDrop } from "@/hooks/useDragAndDrop";

const SidebarTagsSection = () => {
  const location = useLocation();
  const { tags, isLoading, deleteTag } = useTags();
  const { isTagHidden, toggleTagVisibility } = useHiddenTags();
  const [isTagsOpen, setIsTagsOpen] = useState(() => {
    const savedState = localStorage.getItem('sidebar-tags-open');
    return savedState !== null ? savedState === 'true' : true;
  });
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [tagLogicType, setTagLogicType] = useState<"AND" | "OR">("AND");
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTagId, setEditTagId] = useState<string | undefined>(undefined);
  const [editTagData, setEditTagData] = useState<{ name: string; description?: string; color: string } | undefined>(undefined);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTagId, setDeleteTagId] = useState<string>("");
  const [deleteTagName, setDeleteTagName] = useState<string>("");
  
  const { handleDropOnTag, handleDragOver } = useDragAndDrop();
  
  useEffect(() => {
    localStorage.setItem('sidebar-tags-open', isTagsOpen.toString());
  }, [isTagsOpen]);
  
  useEffect(() => {
    const storedTags = localStorage.getItem('selectedTags');
    if (storedTags) {
      try {
        const parsedData = JSON.parse(storedTags);
        if (parsedData && parsedData.tagIds) {
          setSelectedTagIds(Array.isArray(parsedData.tagIds) ? parsedData.tagIds : []);
          setTagLogicType(parsedData.logicType || "AND");
        }
      } catch (error) {
        console.error("Error parsing stored tags:", error);
      }
    }
  }, []);
  
  const tagsWithSelectionState = tags ? tags.map(tag => ({
    ...tag,
    isSecondarySelected: selectedTagIds.includes(tag.id)
  })) : [];

  const handleTagDrop = (e: React.DragEvent, tagId: string, tagName: string) => {
    e.preventDefault();
    console.log(`Content dropped on tag: ${tagName} (${tagId})`);
    
    handleDropOnTag(e, tagId, tagName);
    
    setDropTargetId(null);
  };

  const handleTagDragOver = (e: React.DragEvent, tagId: string) => {
    e.preventDefault();
    handleDragOver(e);
    setDropTargetId(tagId);
  };

  const handleTagDragLeave = () => {
    setDropTargetId(null);
  };
  
  const handleToggleSelection = (tagId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setSelectedTagIds(prevIds => {
      let newIds;
      if (prevIds.includes(tagId)) {
        newIds = prevIds.filter(id => id !== tagId);
      } else {
        newIds = [...prevIds, tagId];
      }
      
      const selectionData = { tagIds: newIds, logicType: tagLogicType };
      localStorage.setItem('selectedTags', JSON.stringify(selectionData));
      
      window.dispatchEvent(new CustomEvent('selectedTagsChanged', { detail: selectionData }));
      
      return newIds;
    });
  };
  
  const handleEditTag = (tag: any, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log("Opening edit dialog for tag:", tag);
    setEditTagId(tag.id);
    setEditTagData({
      name: tag.name,
      description: tag.description || "",
      color: tag.color
    });
    setEditDialogOpen(true);
  };
  
  const handleDeleteTag = (tag: any, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log("Opening delete dialog for tag:", tag);
    setDeleteTagId(tag.id);
    setDeleteTagName(tag.name);
    setDeleteDialogOpen(true);
  };
  
  const handleToggleTagVisibility = (tag: any, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleTagVisibility(tag.id);
  };
  
  const handleClearSelections = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setSelectedTagIds([]);
    
    const selectionData = { tagIds: [], logicType: tagLogicType };
    localStorage.setItem('selectedTags', JSON.stringify(selectionData));
    window.dispatchEvent(new CustomEvent('selectedTagsChanged', { detail: selectionData }));
  };
  
  const handleLogicTypeChange = (newLogicType: "AND" | "OR") => {
    setTagLogicType(newLogicType);
    
    const selectionData = { tagIds: selectedTagIds, logicType: newLogicType };
    localStorage.setItem('selectedTags', JSON.stringify(selectionData));
    window.dispatchEvent(new CustomEvent('selectedTagsChanged', { detail: selectionData }));
  };

  return (
    <div className="w-full">
      <SidebarSection
        title="Tags"
        isOpen={isTagsOpen}
        onOpenChange={setIsTagsOpen}
        action={
          <AddTagButton 
            variant="circle"
            showHoverText={true}
          />
        }
      >
        <SidebarGroupContent className="py-0">
          {selectedTagIds.length > 0 && (
            <TagFilterControls
              logicType={tagLogicType}
              onLogicTypeChange={handleLogicTypeChange}
              onClearSelections={handleClearSelections}
            />
          )}
          
          <SidebarMenu>
            <TagList
              tags={tagsWithSelectionState}
              isLoading={isLoading}
              dropTargetId={dropTargetId}
              locationPathname={location.pathname}
              isTagHidden={isTagHidden}
              onToggleSelection={handleToggleSelection}
              onEditTag={handleEditTag}
              onDeleteTag={handleDeleteTag}
              onToggleTagVisibility={handleToggleTagVisibility}
              onTagDragOver={handleTagDragOver}
              onTagDragLeave={handleTagDragLeave}
              onTagDrop={handleTagDrop}
            />
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarSection>
      
      {/* Edit Tag Dialog */}
      <EditTagDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        tagId={editTagId}
        initialData={editTagData}
      />
      
      {/* Delete Tag Dialog */}
      <DeleteTagDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        tagId={deleteTagId}
        tagName={deleteTagName}
      />
    </div>
  );
};

export default SidebarTagsSection;
